"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "@/contexts/auth-context";
import { useToast } from "@/contexts/toast-context";
import type { AlertKindId } from "@/lib/notifications/kinds";
import { snoozePushPrompt } from "@/lib/push/prompt-snooze";
import { phCapture } from "@/utils/analytics";

export type PushSupport = "unsupported" | "needs_install" | "denied" | "ready";
export type PushSource = "banner" | "panel" | "settings";
export type AlertPreference = { enabled: boolean; settings: { min_ev: number } };
export type Preferences = { entitled: Record<string, boolean>; preferences: Record<string, AlertPreference> };

export const PUSH_CHANNEL = "upsider-push";

const SUBSCRIPTIONS = "/api/notifications/subscriptions";
const PREFERENCES = "/api/notifications/preferences";
const ENABLE_FAILED = "Couldn't turn on alerts for this device.";
const BRAVE_BLOCKED = "Brave blocks push alerts until you turn on “Use Google services for push messaging” in Brave's privacy settings.";

export async function loadPreferences(): Promise<Preferences | null> {
  const response = await fetch(PREFERENCES, { cache: "no-store" }).catch(() => null);
  return response?.ok ? ((await response.json().catch(() => null)) as Preferences | null) : null;
}

export async function savePreference(kind: string, value: AlertPreference, account: string): Promise<boolean> {
  const response = await fetch(PREFERENCES, {
    method: "PUT",
    keepalive: true,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ kind, enabled: value.enabled, settings: value.settings, ...(account ? { profile_id: account } : {}) }),
  }).catch(() => null);
  return response?.ok === true;
}

function detectSupport(): PushSupport {
  const ios = /iPhone|iPad|iPod/.test(navigator.userAgent) || (/Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints > 1);
  const standalone = window.matchMedia("(display-mode: standalone)").matches || (navigator as Navigator & { standalone?: boolean }).standalone === true;
  if (ios && !standalone) return "needs_install";
  if (!("serviceWorker" in navigator) || !("PushManager" in window) || !("Notification" in window)) return "unsupported";
  return Notification.permission === "denied" ? "denied" : "ready";
}

function keyBytes(base64url: string): Uint8Array<ArrayBuffer> {
  return Uint8Array.from(atob(base64url.replace(/-/g, "+").replace(/_/g, "/")), (char) => char.charCodeAt(0));
}

async function localSubscription(): Promise<PushSubscription | null> {
  const registration = await navigator.serviceWorker.getRegistration("/");
  return (await registration?.pushManager.getSubscription()) ?? null;
}

async function accountEndpoints(): Promise<string[] | null> {
  const response = await fetch(SUBSCRIPTIONS, { cache: "no-store" }).catch(() => null);
  const data: { endpoints?: unknown } | null = response?.ok ? await response.json().catch(() => null) : null;
  return Array.isArray(data?.endpoints) ? data.endpoints : null;
}

const PUSH_LOCK = "upsider-push";

async function exclusive(action: () => Promise<void>): Promise<void> {
  await navigator.locks.request(PUSH_LOCK, action);
}

async function send(method: "POST" | "DELETE", body: unknown): Promise<void> {
  const response = await fetch(SUBSCRIPTIONS, { method, headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  if (!response.ok) throw new Error(`${method} ${SUBSCRIPTIONS} ${response.status}`);
}

export function usePushSubscription() {
  const { showToast } = useToast();
  const { user } = useAuth();
  const accountRef = useRef(user?.id ?? "");
  accountRef.current = user?.id ?? "";
  const [support, setSupport] = useState<PushSupport | null>(null);
  const [endpoints, setEndpoints] = useState<string[] | null>([]);
  const [localEndpoint, setLocalEndpoint] = useState<string | null>(null);
  const [preferences, setPreferences] = useState<Preferences | null>(null);
  const [busy, setBusy] = useState(true);
  const channelRef = useRef<BroadcastChannel | null>(null);
  const generationRef = useRef(0);

  const refresh = useCallback(async () => {
    const generation = ++generationRef.current;
    const next = detectSupport();
    setSupport(next);
    const [local, account, saved] = await Promise.all([
      next === "ready" ? localSubscription().catch(() => null) : null,
      accountEndpoints(),
      loadPreferences(),
    ]);
    if (generation !== generationRef.current) return;
    setLocalEndpoint(local?.endpoint ?? null);
    setEndpoints(account);
    setPreferences(saved);
  }, []);

  const run = useCallback(
    async (action: () => Promise<void>, failure: string) => {
      setBusy(true);
      try {
        await exclusive(action);
      } catch {
        showToast(failure, "error");
      }
      await refresh();
      setBusy(false);
      channelRef.current?.postMessage(null);
    },
    [refresh, showToast],
  );

  useEffect(() => {
    refresh().finally(() => setBusy(false));
    if (typeof BroadcastChannel === "undefined") return;
    const channel = new BroadcastChannel(PUSH_CHANNEL);
    channel.onmessage = () => void refresh();
    channelRef.current = channel;
    return () => {
      channelRef.current = null;
      channel.close();
    };
  }, [refresh]);

  const enable = useCallback(
    (source: PushSource, kind?: AlertKindId) => {
      const account = accountRef.current;
      const owner = account ? { profile_id: account } : {};
      const permission = Notification.requestPermission();
      return run(async () => {
        if ((await permission) !== "granted") return;
        const key = process.env.NEXT_PUBLIC_VAPID_PUBLIC_KEY;
        if (!key) throw new Error("NEXT_PUBLIC_VAPID_PUBLIC_KEY is not set");
        await navigator.serviceWorker.register("/sw.js");
        const registration = await navigator.serviceWorker.ready;
        const existed = (await registration.pushManager.getSubscription()) !== null;
        const subscription = await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(key) }).catch(() => null);
        if (!subscription) {
          showToast("brave" in navigator ? BRAVE_BLOCKED : ENABLE_FAILED, "error");
          return;
        }
        if (account && accountRef.current !== account) return;
        await send("POST", { ...subscription.toJSON(), ...owner });
        if (kind) {
          const saved = (await loadPreferences())?.preferences[kind];
          if (account && accountRef.current !== account) return;
          if (!saved || !saved.enabled) {
            const optedIn = saved ? await savePreference(kind, { enabled: true, settings: saved.settings }, account) : false;
            if (!optedIn) {
              if (!existed && accountRef.current === account) {
                await send("DELETE", { endpoint: subscription.endpoint, ...owner }).then(() => subscription.unsubscribe(), () => {});
              }
              throw new Error(`${kind} opt-in failed`);
            }
          }
        }
        phCapture("push_enabled", { source, kind: kind ?? null });
      }, ENABLE_FAILED);
    },
    [run, showToast],
  );

  const disable = useCallback(() => {
    const account = accountRef.current;
    return run(async () => {
      const subscription = await localSubscription();
      if (!subscription) return;
      await send("DELETE", { endpoint: subscription.endpoint, ...(account ? { profile_id: account } : {}) });
      await subscription.unsubscribe();
      if (account) snoozePushPrompt(account);
    }, "Couldn't turn off alerts for this device.");
  }, [run]);

  const disableAll = useCallback(() => {
    const account = accountRef.current;
    return run(async () => {
      await send("DELETE", { all: true, ...(account ? { profile_id: account } : {}) });
      const local = support === "ready" ? await localSubscription() : null;
      if (local && endpoints?.includes(local.endpoint)) await local.unsubscribe();
      if (account) snoozePushPrompt(account);
    }, "Couldn't stop alerts on all devices.");
  }, [run, support, endpoints]);

  const optedIn = useMemo<ReadonlySet<string> | null>(
    () => (preferences ? new Set(Object.keys(preferences.preferences).filter((kind) => preferences.preferences[kind].enabled)) : null),
    [preferences],
  );

  return {
    support,
    loadFailed: endpoints === null,
    enabledOnThisDevice: localEndpoint !== null && endpoints !== null && endpoints.includes(localEndpoint),
    devices: endpoints?.length ?? 0,
    busy,
    optedIn,
    enable,
    disable,
    disableAll,
  };
}
